import type { Subscription } from "zen-observable-ts";
import { readonly } from "vue";
import {
  MyAuthorizationChangedDocument,
  MyAuthorizationDocument,
  type MyAuthorizationChangedSubscription,
  type MyAuthorizationQuery,
} from "@/graphql";
import { apolloClient } from "@/graphql/apollo";
import {
  authorizationLoading as loading,
  authorizationReady as ready,
  authorizationSnapshot as snapshot,
  authorizationStale as stale,
  hasRight,
  hasRightOnAnySite,
} from "./authorizationState";

export { hasRight, hasRightOnAnySite } from "./authorizationState";

let started = false;
let requiredRevision = 0;
let refreshInFlight: Promise<boolean> | null = null;
let refreshAgain = false;
let epoch = 0;
let notificationSubscription: Subscription | null = null;
let fallbackTimer: ReturnType<typeof setInterval> | null = null;

export function useAuthorization() {
  return {
    snapshot: readonly(snapshot),
    stale: readonly(stale),
    loading: readonly(loading),
    ready,
    hasRight,
    hasRightOnAnySite,
    refresh: refreshAuthorization,
  };
}

export async function refreshAuthorization({
  invalidate = true,
}: { invalidate?: boolean } = {}): Promise<boolean> {
  if (!started) return false;
  if (refreshInFlight) {
    if (invalidate) {
      stale.value = true;
      refreshAgain = true;
    }
    return refreshInFlight;
  }
  if (invalidate || snapshot.value === null) {
    stale.value = true;
    loading.value = true;
  }
  const requestEpoch = epoch;
  const request = (async () => {
    try {
      const { data } = await apolloClient.query<MyAuthorizationQuery>({
        query: MyAuthorizationDocument,
        fetchPolicy: "network-only",
      });
      const next = data.MyAuthorization;
      if (requestEpoch !== epoch) return false;
      if (next.revision < Math.max(requiredRevision, snapshot.value?.revision ?? -1)) {
        stale.value = true;
        return false;
      }
      snapshot.value = next;
      stale.value = false;
      return true;
    } catch {
      if (requestEpoch === epoch) stale.value = true;
      return false;
    } finally {
      if (requestEpoch === epoch) loading.value = false;
    }
  })();
  refreshInFlight = request;
  try {
    return await request;
  } finally {
    if (refreshInFlight === request) refreshInFlight = null;
    if (refreshAgain && started && requestEpoch === epoch) {
      refreshAgain = false;
      void refreshAuthorization();
    }
  }
}

function subscribeToChanges() {
  notificationSubscription?.unsubscribe();
  notificationSubscription = apolloClient
    .subscribe<MyAuthorizationChangedSubscription>({
      query: MyAuthorizationChangedDocument,
    })
    .subscribe({
      next({ data }) {
        const revision = data?.MyAuthorizationChanged;
        if (revision == null || revision <= (snapshot.value?.revision ?? -1)) return;
        requiredRevision = Math.max(requiredRevision, revision);
        stale.value = true;
        void refreshAuthorization();
      },
      error() {
        notificationSubscription = null;
        stale.value = true;
      },
    });
}

function onConnectionRestored() {
  if (!started) return;
  if (!notificationSubscription) subscribeToChanges();
  void refreshAuthorization({ invalidate: false });
}

function onForeground() {
  if (started && document.visibilityState === "visible") {
    void refreshAuthorization({ invalidate: false });
  }
}

export async function initializeAuthorization() {
  if (started) return refreshAuthorization();
  started = true;
  subscribeToChanges();
  window.addEventListener("lisa-graphql-ws-connected", onConnectionRestored);
  document.addEventListener("visibilitychange", onForeground);
  fallbackTimer = setInterval(() => {
    // Validate a current snapshot without unmounting protected pages every minute.
    // A failed check or observed revision mismatch still fails closed.
    void refreshAuthorization({ invalidate: false });
  }, 60_000);
  return refreshAuthorization();
}

export function stopAuthorization() {
  epoch += 1;
  started = false;
  refreshInFlight = null;
  refreshAgain = false;
  loading.value = false;
  notificationSubscription?.unsubscribe();
  notificationSubscription = null;
  if (fallbackTimer) clearInterval(fallbackTimer);
  fallbackTimer = null;
  window.removeEventListener("lisa-graphql-ws-connected", onConnectionRestored);
  document.removeEventListener("visibilitychange", onForeground);
  snapshot.value = null;
  stale.value = true;
  requiredRevision = 0;
}
